// BFSG
import * as React from 'react'
import styled from 'styled-components'
import { Product } from 'modules/listing/types'
import theme from 'theme'
import Link from 'theme/atoms/Link'
import ArrowRight from 'assets/link-arrow.svg'
import Price, * as Prices from 'theme/atoms/Price'
import useTranslations from 'hooks/useTranslations'
import getProductLink from 'utils/getProductLink'
import useWishlist from 'hooks/account/lists/useWishlist'
import HeartEmpty from 'assets/Heart-Empty.svg'
import HeartFilled from 'assets/Heart-Filled.svg'
import Rating from 'theme/molecules/Rating'
import Share from 'theme/molecules/Share'

type Props = {
  hit: Product
  listname: string
  listPosition: number
  listid: string
  evtSource?: string
}

export default function Description(props: Props) {
  const t = useTranslations<'templates-Series'>()
  const wishlist = useWishlist()

  const hasStrikePrice = Prices.PriceHasStrikePrice(props.hit, 1)
  const listItemId =
    (typeof window !== 'undefined' && window.location.href) || ''

  return (
    <Wrapper className="Description">
      <div className="top">
        <div className="title-subtitle">
          <h5>
            <Link
              to={getProductLink(props.hit)}
              aria-label={t.asText(false, 'sku_detail_link')}
            >
              {props.hit.title}
            </Link>
          </h5>
          <h6>{props.hit.subtitle}</h6>
        </div>
        <button
          className="mobile-heart"
          data-cy-handle="mobile-wishlist-toggle"
          onClick={() =>
            wishlist.toggleSku(props.hit.sw6Uuid, {
              variant: props.hit,
              listid: props.listPosition,
              listname: props.listname,
              item_list_id: listItemId
            })
          }
        >
          {wishlist.sw6uuids.has(props.hit.sw6Uuid) ? (
            <HeartFilled data-cy-state="wishlist-added:mobile" />
          ) : (
            <HeartEmpty />
          )}
        </button>
      </div>

      {props.hit.productReviews && (
        <Rating
          data-cy-state="has-rating"
          containerID={props.hit.containerID}
          ratingAverage={props.hit.productReviews.productReviewAvg}
          ratingCount={props.hit.productReviews.productReviewCount}
          clickable
          evtSource={props.evtSource}
        />
      )}

      <div className="prices">
        <div className="piece-price">
          <span className="price-wrapper">
            <Price prices={props.hit.prices} logic="piece" />
            {Prices.PriceHasStrikePrice(props.hit, 1) && (
              <span className="strike" data-cy-state="has-strike-price">
                <Prices.PriceStrike prices={props.hit.prices} logic="piece" />
              </span>
            )}
          </span>
          <span className="info-wrapper">
            <span className="label">{t('sku_piece_price_label')}</span>
            <span className="info">
              <Prices.PriceB2BGrossInfo
                prices={props.hit.prices}
                scalePrice={props.hit.priceRules}
                amount={1}
                logic="piece"
              />
              <Prices.PriceB2CNetInfo
                prices={props.hit.prices}
                scalePrice={props.hit.priceRules}
                amount={1}
                logic="piece"
              />
            </span>
            <div className="additional-info-ecoTax">
              {props.hit.unit.purchaseUnit === 1 &&
                (props.hit.prices.ecoTaxNet ||
                  props.hit.prices.ecoTaxGross) && (
                  <Prices.EcoTax
                    prices={props.hit.prices}
                    amount={1}
                    data-cy-state={'hasEcoTax'}
                  />
                )}
            </div>
          </span>
        </div>
        {props.hit.unit.purchaseUnit > 1 && (
          <div className="pack-price" data-cy-state="has-pack-price">
            <span className="price-wrapper">
              <Price prices={props.hit.prices} logic="pack" />
              {hasStrikePrice && (
                <span className="strike" data-cy-state="has-strike-price">
                  <Prices.PriceStrike prices={props.hit.prices} logic="pack" />
                </span>
              )}
            </span>
            <span className="info-wrapper">
              <span className="label">
                {props.hit.unit.purchaseUnit} {props.hit.unit.unitName} /{' '}
                {props.hit.unit.packUnit}
              </span>
              <span className="info">
                <Prices.PriceB2BGrossInfo
                  prices={props.hit.prices}
                  scalePrice={props.hit.priceRules}
                  amount={1}
                  logic="pack"
                />
                <Prices.PriceB2CNetInfo
                  prices={props.hit.prices}
                  scalePrice={props.hit.priceRules}
                  amount={1}
                  logic="pack"
                />
              </span>
              <div className="additional-info-ecoTax">
                {(props.hit.prices.ecoTaxNet ||
                  props.hit.prices.ecoTaxGross) && (
                  <Prices.EcoTax
                    prices={props.hit.prices}
                    amount={1}
                    data-cy-state={'hasEcoTax'}
                  />
                )}
              </div>
            </span>
          </div>
        )}
      </div>

      <div className="meta">
        <div className="sku">
          {t('sku_article_number_label')}
          <Link
            to={getProductLink(props.hit)}
            aria-label={t.asText(false, 'sku_detail_link')}
          >
            {` `}i{props.hit.sku}
          </Link>
        </div>

        <div className="desktop-meta">
          <button
            className="desktop-heart"
            data-cy-handle="desktop-mobile-wishlist-toggle"
            onClick={() =>
              wishlist.toggleSku(props.hit.sw6Uuid, {
                variant: props.hit,
                listid: props.listPosition,
                listname: props.listname,
                item_list_id: listItemId
              })
            }
          >
            {wishlist.sw6uuids.has(props.hit.sw6Uuid) ? (
              <>
                <HeartFilled data-cy-state="wishlist-added:desktop" />
                <span>{t('sku_remove_from_wishlist')}</span>
              </>
            ) : (
              <>
                <HeartEmpty />
                <span>{t('sku_add_to_wishlist')}</span>
              </>
            )}
          </button>
          <div className="desktop-share">
            <Share
              productName={props.hit.title}
              buttonType="symbol_and_text"
              linkToShare={getProductLink(props.hit)}
              internalLink
            />
          </div>
          <Link className="detail-link" to={getProductLink(props.hit)}>
            <ArrowRight />
            {t('sku_detail_link')}
          </Link>
        </div>

        <div className="mobile-meta">
          <Link className="detail-link" to={getProductLink(props.hit)}>
            <ArrowRight />
            {t('sku_detail_link')}
          </Link>

          <div className="mobile-share">
            <Share
              productName={props.hit.title}
              buttonType="symbol_and_text"
              linkToShare={getProductLink(props.hit)}
              internalLink
            />
          </div>
        </div>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  > .top {
    display: flex;
    justify-content: space-between;
    gap: ${theme.spacing('xxs')};

    > .title-subtitle {
      > h5 {
        ${theme.ty('rc-base')};
      }
      > h6 {
        ${theme.ty('r-xs')};
        color: ${theme.colors.shade.b2};
      }

      @media (min-width: ${theme.ms.LG}px) {
        > h5 {
          ${theme.ty('rc-2xl')};
        }
        > h6 {
          ${theme.ty('r-s')};
          color: ${theme.colors.shade.b3};
        }
      }
    }

    > .mobile-heart {
      cursor: pointer;
      border: 1px solid ${theme.colors.shade.b2};
      ${theme.rounding('m')};
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;

      > svg {
        height: 20px;
        width: 25px;
        transform: scale(1.4);
      }

      @media (min-width: ${theme.ms.LG}px) {
        display: none;
      }
    }
  }

  > .Rating,
  > .prices,
  > .meta {
    margin-top: ${theme.spacing('xs')};
  }

  > .prices {
    > .piece-price,
    > .pack-price {
      > .price-wrapper {
        ${theme.ty('r-s', 'bold')};

        > .strike {
          ${theme.ty('r-xs')};
          color: ${theme.colors.shade.b3};
          padding-left: ${theme.spacing('xxs')};
          text-decoration: line-through;
        }
      }

      > .info-wrapper {
        > .info,
        > .label,
        > .additional-info-ecoTax {
          ${theme.ty('r-xs')};
          padding-left: ${theme.spacing('xxs')};
          color: ${theme.colors.shade.b3};
        }
      }
    }

    > .croatiaCurrency {
      width: 52px;
      height: 14px;
      font-family: 'Roboto';
      font-weight: 700;
      font-size: 10px;
      line-height: 14px;
      color: #625d52;
    }

    @media (min-width: ${theme.ms.MD}px) {
      > .piece-price,
      > .pack-price {
        > .price-wrapper {
          ${theme.ty('r-base', 'bold')};

          > .strike {
            ${theme.ty('r-s')};
          }
        }

        > .info-wrapper {
          > .info,
          > .label,
          > .additional-info-ecoTax {
            ${theme.ty('r-s')};
          }
        }
      }
    }

    @media (min-width: ${theme.ms.LG}px) {
      display: flex;

      > .piece-price,
      > .pack-price {
        &.pack-price {
          margin-left: ${theme.spacing('l')};
        }

        > .price-wrapper {
          flex: 1;
          ${theme.ty('r-2xl', 'bold')};

          > .strike {
            ${theme.ty('r-base')};
          }
        }

        > .info-wrapper {
          display: block;

          > .info,
          > .label,
          > .additional-info-ecoTax {
            ${theme.ty('r-base')};
            padding-left: ${theme.spacing('xxs')};
            &.label {
              padding-left: 0;
            }
          }
        }
      }
    }
  }

  > .meta {
    margin-top: ${theme.spacing('m')};

    > .desktop-meta {
      display: none;

      @media (min-width: ${theme.ms.LG}px) {
        display: flex;
        align-items: center;
        gap: ${theme.spacing('s')};
      }

      > .desktop-heart {
        display: flex;
        gap: ${theme.spacing('xxs')};
        align-items: center;
        ${theme.ty('rc-base', 'bold')};
        cursor: pointer;
      }

      > .detail-link {
        ${theme.ty('rc-base', 'bold')};

        > svg {
          margin-right: 5px;
          width: 12px;
        }
      }
    }

    > .mobile-meta {
      display: flex;
      gap: ${theme.spacing('xs')};

      @media (min-width: ${theme.ms.LG}px) {
        display: none;
      }

      > .detail-link {
        ${theme.ty('rc-base', 'bold')};

        > svg {
          margin-right: 5px;
          width: 12px;
        }
      }
    }

    > .sku {
      ${theme.ty('r-xs')};
      color: ${theme.colors.shade.b2};

      > a {
        color: ${theme.colors.shade.b2};
      }
    }

    > .mobile-share {
      > svg {
        margin-right: 5px;
        width: 12px;
      }

      @media (min-width: ${theme.ms.LG}px) {
        display: none;
      }
    }

    @media (min-width: ${theme.ms.MD}px) {
      margin-top: ${theme.spacing('ml')};

      > .sku {
        ${theme.ty('r-s')};
      }
    }

    @media (min-width: ${theme.ms.LG}px) {
      display: flex;
      flex-wrap: wrap;
      gap: ${theme.spacing('xs')};

      > .sku {
        flex: 1 100%;
      }

      > .detail-link {
        flex: 1;
        margin-top: 0;
        text-align: right;
      }
    }
  }
`
